<template>
  <div class="flex-1">
    <bb-public-table
      data-testid="sign-pending-table"
      :columns="labeledColumns"
      :rows="tableRows"
      :footer-columns="footerColumns"
      :footer-rows="footerRows"
      :rows-state.sync="rowsState"
      :subrow-columns="subrowColumns"
      :subrow-toggle-label="translations.table.toggleDetails"
    >
      <template #header="{ column }">
        <span class="d-block f-nowrap">
          {{ castArray(column.label)[0] }}
        </span>
        <span
          v-if="castArray(column.label)[1]"
          class="d-block f-nowrap f-color-gray-70"
        >
          {{ column.label[1] }}
        </span>
      </template>
      <template #cell="{ column, value }">
        <template v-if="column.key === 'status'">
          <p
            class="f-gotham-bold f-small"
            :class="getPaymentStatusColor(value)"
          >
            {{ translations.status[value[0]] || value[0] }}
          </p>
          <p
            v-if="value[1]"
            class="f-gotham-bold f-color-gray-70 f-tiny"
          >
            {{ value[1] }}
          </p>
        </template>
        <template v-else>
          <p class="f-gotham-bold f-small f-color-gray">
            {{ castArray(value)[0] }}
          </p>
          <p
            v-if="castArray(value)[1]"
            class="f-gotham-bold f-color-gray-70 f-tiny"
          >
            {{ value[1] }}
          </p>
        </template>
      </template>
      <template #subrowCell="{ column, row, rowIndex }">
        <template v-if="column.key === 'details'">
          <p class="m-b-15 f-gotham-medium f-small f-color-navy p-l-10">
            {{ translations.table.paymentDetails }}
          </p>
          <dl style="display: grid; grid-template-columns: auto 1fr; gap: 5px 40px;" data-testid="pending-details"
              class="p-l-10">
            <template v-for="item in subRowTemplate">
              <template v-for="(label, index) in castArray(columnLabels[item])">
                <dt
                  :key="`${item}-desc-${index + 1}`"
                  class="f-smallest f-gotham-bold f-color-gray"
                >
                  {{ label }}
                </dt>
                <dd
                  :key="`${item}-value-${index + 1}`"
                  class="f-smallest f-gotham-book f-color-gray"
                >
                  {{ castArray(row[item])[index] || '-' }}
                </dd>
              </template>
            </template>
          </dl>
          <bb-button
            data-testid="pending-close-button"
            color="black"
            size="sm"
            :label="translations.button.close"
            inverted
            style="position: absolute; top: 15px; right: 15px;"
            @click.stop="closeSubrow(rowIndex)"
          />
        </template>
      </template>
      <template #footerCell="{ value }">
        <p class="m-y-15 f-gotham-bold f-color-gray f-small">
          {{ value }}
        </p>
      </template>
    </bb-public-table>
    <div class="d-flex justify-content-end gap-15 m-t-15">
      <bb-button
        data-testid="pending-go-back-button"
        :label="translations.button.returnBack"
        size="sm"
        color="gray"
        inverted
        @click="goBack"
      />
      <bb-button
        data-testid="pending-sign-button"
        :label="translations.button.accept"
        v-bind="buttonProps"
        size="sm"
        @click="onSign"
      />
    </div>
  </div>
</template>

<script>
import { castArray } from 'lodash'
import pendingTableMixin from '@account/views/payments/pending/pending-table.mixin'
import { formatMoneyWithCurrency } from '@/plugins/numformat'
import { formatDate } from '@/plugins/dateFormatters'

export default {
  name: 'sign-pending-table-desktop',
  mixins: [pendingTableMixin],
  props: {
    buttonProps: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      rowsState: {},
      subRowTemplate: [
        'fromIban',
        'dateTime',
        'beneficiary',
        'formattedAmount',
        'description',
        'referenceNumber',
        'status'
      ],
      footerColumns: [
        {
          offset: 3,
          type: 'th',
          key: 'label',
          class: 'border-left-radius-sm'
        },
        {
          key: 'amount',
          class: 'f-nowrap f-align-right p-r-25'
        }
      ]
    }
  },
  computed: {
    subrowColumns () {
      return [
        {
          key: 'details',
          class: 'relative p-y-15',
          td: {
            colspan: 6,
            'aria-label': this.translations.row.subPaymentDetails
          }
        }
      ]
    },
    labeledColumns () {
      const columns = [
        {
          key: 'beneficiary',
          class: 'p-l-25'
        },
        { key: 'dateTime' },
        { key: 'descriptionAndReference' },
        {
          key: 'status',
          class: 'f-nowrap'
        },
        {
          key: 'formattedAmount',
          class: 'f-nowrap f-align-right p-r-25'
        }
      ]

      return columns.map(column => ({
        ...column,
        label: this.columnLabels[column.key]
      }))
    },
    columnLabels () {
      const description = this.translations.column.description
      const referenceNumber = this.translations.column.referenceNumber

      return {
        fromIban: this.translations.column.fromIban,
        dateTime: this.translations.column.orderDate,
        status: this.translations.column.orderStatus,
        beneficiary: [
          this.translations.column.recipientName,
          this.translations.column.recipientIban
        ],
        descriptionAndReference: [
          description,
          referenceNumber
        ],
        referenceNumber,
        description,
        formattedAmount: this.translations.column.amount
      }
    },
    footerRows () {
      return [
        {
          label: this.translations.footer.totalAmount,
          amount: this.sumForSigning
        }
      ]
    },
    tableRows () {
      return this.pendingPayments.map(pending => ({
        id: pending.id,
        fromIban: pending.fromIban,
        dateTime: formatDate(pending.orderDate),
        status: [pending.orderStatus, this.translations.statusDescription[pending.orderStatus] || ''],
        beneficiary: [
          pending.recipientName,
          pending.recipientIban
        ],
        description: pending.description,
        referenceNumber: pending.referenceNumber,
        descriptionAndReference: [pending.description || '-', pending.referenceNumber || '-'],
        amount: pending.amount,
        formattedAmount: formatMoneyWithCurrency(pending.amount, pending.currencyCode, this.locale)
      }))
    }
  },
  methods: {
    castArray,
    closeSubrow (rowIndex) {
      this.$set(this.rowsState, rowIndex, false)
    }
  }
}
</script>
